import { useState, useRef } from 'react'
import type { ReactNode } from 'react'
import { Button } from '@/components/Button'
import { FilterButton } from '@/components/FilterButton'
import {
	FilterContent,
	MultiSelectContent,
	SingleSelectContent,
	RangeContent,
	DateContent,
	MULTI_SELECT_MIN_WIDTH,
	SINGLE_SELECT_MIN_WIDTH,
	RANGE_MIN_WIDTH,
	DATE_MIN_WIDTH
} from '@/components/FilterContent'
import type { FilterOption } from '@/components/FilterContent'
import { ToggleButtonGroup, ToggleButton } from '@/components/ToggleButtonGroup'
import { Tooltip } from '@/components/Tooltip'
import { Icon } from '@/components/Icon'
import { useOverflowFilters } from '@/hooks/useOverflowFilters'
import { PRICE_STEP } from '@/data/config'
import type { CustomFilterConfig } from '@/types/api'

type ViewMode = 'grid' | 'split' | 'map'

export interface FilterBarProps {
	viewMode: ViewMode
	onViewModeChange: (mode: ViewMode) => void
	/** Whether the split view option is available (e.g. hidden on mobile) */
	showSplitView?: boolean
	/* Filter state */
	beds: string[]
	onBedsChange: (beds: string[]) => void
	bedOptions: FilterOption[]
	showPriceFilter?: boolean
	showDateFilter?: boolean
	priceMin: number | undefined
	priceMax: number | undefined
	onPriceMinChange: (value: number | undefined) => void
	onPriceMaxChange: (value: number | undefined) => void
	priceMinBound: number
	priceMaxBound: number
	moveIn: string
	onMoveInChange: (value: string) => void
	calendarMonth: number
	calendarYear: number
	onCalendarMonthChange: (month: number, year: number) => void
	/* Custom filters */
	customFilterConfigs: CustomFilterConfig[]
	customFilterSelections: Record<string, string[]>
	onCustomFilterSelectionsChange: (selections: Record<string, string[]>) => void
	activeFilterCount: number
	onClearAll: () => void
	onAllFiltersClick: () => void
	/** Called after any individual filter popover commits, with the full new filter state. */
	onFiltersApply?: (beds: string[], priceMin: number | undefined, priceMax: number | undefined, moveIn: string, sort?: string, customSelections?: Record<string, string[]>) => void
	className?: string
}

interface FilterSlotProps {
	visible?: boolean
	showFilters: boolean
	slotRef: (el: HTMLDivElement | null) => void
	children: ReactNode
}

/** Measured wrapper for a single filter button; renders nothing when the filter is disabled. */
function FilterSlot ({ visible = true, showFilters, slotRef, children }: FilterSlotProps) {
	if (!visible) return null

	return (
		<div
			ref={slotRef}
			className={
				showFilters ? 'spaces:shrink-0' : 'spaces:absolute spaces:invisible spaces:pointer-events-none'
			}
			aria-hidden={!showFilters}
		>
			{children}
		</div>
	)
}

/** Format a multi-select label as the first selected option's label */
function formatMultiLabel (
	selected: string[],
	options: { value: string; label: string }[]
): string | undefined {
	if (selected.length === 0) return undefined
	return options.find(o => o.value === selected[0])?.label ?? selected[0]
}

/** Format an ISO date string as short "Mon DD" */
function formatShortDate (iso: string): string | undefined {
	if (!iso) return undefined
	const d = new Date(iso + 'T00:00:00')
	if (isNaN(d.getTime())) return iso
	return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function FilterBar ({
	viewMode,
	onViewModeChange,
	showSplitView = true,
	beds,
	onBedsChange,
	bedOptions,
	showPriceFilter = true,
	showDateFilter = true,
	priceMin,
	priceMax,
	onPriceMinChange,
	onPriceMaxChange,
	priceMinBound,
	priceMaxBound,
	moveIn,
	onMoveInChange,
	calendarMonth,
	calendarYear,
	onCalendarMonthChange,
	customFilterConfigs,
	customFilterSelections,
	onCustomFilterSelectionsChange,
	activeFilterCount,
	onClearAll,
	onAllFiltersClick,
	onFiltersApply,
	className = ''
}: FilterBarProps) {
	const bedsLabel = formatMultiLabel(beds, bedOptions)

	const priceLabel =
		priceMin != null && priceMax != null
			? `$${priceMin.toLocaleString()}–$${priceMax.toLocaleString()}`
			: priceMin != null
			? `$${priceMin.toLocaleString()}+`
			: priceMax != null
			? `Up to $${priceMax.toLocaleString()}`
			: undefined

	const dateLabel = formatShortDate(moveIn)

	// --- Custom filter labels ---
	const customFilterLabels: Record<string, string | undefined> = {}
	for (const cf of customFilterConfigs) {
		const selected = customFilterSelections[cf.slug] ?? []
		if (cf.type === 'select') {
			customFilterLabels[cf.slug] = selected.length > 0
				? cf.options.find(o => o.value === selected[0])?.label ?? selected[0]
				: undefined
		} else {
			customFilterLabels[cf.slug] = formatMultiLabel(selected, cf.options)
		}
	}

	// --- Popover open state (one per filter) ---
	const [bedsOpen, setBedsOpen] = useState(false)
	const [priceOpen, setPriceOpen] = useState(false)
	const [dateOpen, setDateOpen] = useState(false)
	const [customFiltersOpen, setCustomFiltersOpen] = useState<Record<string, boolean>>(() => {
		const m: Record<string, boolean> = {}
		for (const cf of customFilterConfigs) m[cf.slug] = false
		return m
	})

	// --- Draft state (committed only on Save) ---
	const [bedsDraft, setBedsDraft] = useState<string[]>(beds)
	const [priceMinDraft, setPriceMinDraft] = useState<number | undefined>(
		priceMin
	)
	const [priceMaxDraft, setPriceMaxDraft] = useState<number | undefined>(
		priceMax
	)
	const [moveInDraft, setMoveInDraft] = useState(moveIn)
	const [calendarMonthDraft, setCalendarMonthDraft] = useState(calendarMonth)
	const [calendarYearDraft, setCalendarYearDraft] = useState(calendarYear)
	const [customFilterDrafts, setCustomFilterDrafts] = useState<Record<string, string[]>>(() => ({ ...customFilterSelections }))

	// --- Open-change handlers: snapshot committed state into draft on open ---
	const handleBedsOpenChange = (open: boolean) => {
		if (open) setBedsDraft(beds)
		setBedsOpen(open)
	}
	const handlePriceOpenChange = (open: boolean) => {
		if (open) {
			setPriceMinDraft(priceMin)
			setPriceMaxDraft(priceMax)
		}
		setPriceOpen(open)
	}
	const handleDateOpenChange = (open: boolean) => {
		if (open) {
			setMoveInDraft(moveIn)
			setCalendarMonthDraft(calendarMonth)
			setCalendarYearDraft(calendarYear)
		}
		setDateOpen(open)
	}

	const handleCustomFilterOpenChange = (slug: string, open: boolean) => {
		if (open) setCustomFilterDrafts(prev => ({ ...prev, [slug]: customFilterSelections[slug] ?? [] }))
		setCustomFiltersOpen(prev => ({ ...prev, [slug]: open }))
	}

	// --- Overflow measurement refs ---
	const containerRef = useRef<HTMLDivElement>(null)
	const itemRefs = useRef<(HTMLDivElement | null)[]>([])
	const measureFixedRef = useRef<HTMLDivElement>(null)

	// useOverflowFilters stops at the first empty slot, so slots must stay contiguous.
	const priceSlot = 1
	const dateSlot = priceSlot + (showPriceFilter ? 1 : 0)
	const customSlot = dateSlot + (showDateFilter ? 1 : 0)
	const clearSlot = customSlot + customFilterConfigs.length
	itemRefs.current.length = clearSlot + 1

	const { allFit, measured } = useOverflowFilters(
		containerRef,
		itemRefs,
		measureFixedRef,
		[
			bedsLabel,
			priceLabel,
			dateLabel,
			beds.length,
			...customFilterConfigs.map(cf => customFilterLabels[cf.slug]),
			...customFilterConfigs.map(cf => (customFilterSelections[cf.slug] ?? []).length)
		]
	)

	const showFilters = showSplitView ? allFit : false
	const showAllFiltersBtn = showSplitView ? !allFit : true

	return (
		<div
			ref={containerRef}
			className={`spaces-filter-bar spaces:flex spaces:items-center spaces:gap-8 spaces:bg-canvas-base spaces:border spaces:border-canvas-3 spaces:rounded-24 spaces:pl-12 spaces:pr-8 spaces:py-8 ${className}`}
			style={!measured && showSplitView ? { visibility: 'hidden' } : undefined}
		>
			{/* All Filters button — shown when filters don't fit */}
			<div
				className={showAllFiltersBtn ? 'spaces:shrink-0' : 'spaces:hidden'}
				aria-hidden={!showAllFiltersBtn}
			>
				<button
					type='button'
					onClick={onAllFiltersClick}
					className={`spaces-filter-all spaces:inline-flex spaces:items-center spaces:gap-8 spaces:py-7 spaces:rounded-12 spaces:border spaces:border-canvas-2 spaces:bg-canvas-base spaces:cursor-pointer spaces:transition-colors spaces:hover:border-canvas-3 spaces:focus-visible:outline-canvas-base spaces:focus-visible:ring-2 spaces:focus-visible:ring-brand-base spaces:focus-visible:ring-offset-2 ${
						activeFilterCount > 0 ? 'spaces:pl-15 spaces:pr-7' : 'spaces:px-15'
					}`}
				>
					<Icon name='FilterHandles' size={20} className='spaces:text-content-2' />
					<span className='spaces:text-16/24 spaces:font-default spaces:text-content-2 spaces:whitespace-nowrap'>
						Filter & Sort
					</span>
					{activeFilterCount > 0 && (
						<span className='spaces:inline-flex spaces:items-center spaces:justify-center spaces:min-w-24 spaces:p-4 spaces:rounded-4 spaces:bg-brand-base spaces:text-brand-on-base spaces:text-14/16 spaces:font-default spaces:text-center'>
							{activeFilterCount}
						</span>
					)}
				</button>
			</div>

			{/* Individual filter buttons — all shown or all hidden */}
			<FilterSlot
				showFilters={showFilters}
				slotRef={el => {
					itemRefs.current[0] = el
				}}
			>
				<FilterButton
					label='Beds'
					icon='Bed'
					minWidth={MULTI_SELECT_MIN_WIDTH}
					activeLabel={bedsLabel}
					additionalCount={beds.length > 1 ? beds.length - 1 : undefined}
					onClear={() => {
						onBedsChange([])
						setBedsOpen(false)
						onFiltersApply?.([], priceMin, priceMax, moveIn)
					}}
					open={bedsOpen}
					onOpenChange={handleBedsOpenChange}
				>
					<FilterContent
						title='Bedrooms'
						onClear={() => setBedsDraft([])}
						onCancel={() => setBedsOpen(false)}
						onSave={() => {
							onBedsChange(bedsDraft)
							setBedsOpen(false)
							onFiltersApply?.(bedsDraft, priceMin, priceMax, moveIn)
						}}
					>
						<MultiSelectContent
							options={bedOptions}
							selected={bedsDraft}
							onChange={setBedsDraft}
							ariaLabel='Bedrooms'
						/>
					</FilterContent>
				</FilterButton>
			</FilterSlot>

			<FilterSlot
				visible={showPriceFilter}
				showFilters={showFilters}
				slotRef={el => {
					itemRefs.current[priceSlot] = el
				}}
			>
				<FilterButton
					label='Price'
					icon='DollarSign'
					minWidth={RANGE_MIN_WIDTH}
					activeLabel={priceLabel}
					onClear={() => {
						onPriceMinChange(undefined)
						onPriceMaxChange(undefined)
						setPriceOpen(false)
						onFiltersApply?.(beds, undefined, undefined, moveIn)
					}}
					open={priceOpen}
					onOpenChange={handlePriceOpenChange}
				>
					<FilterContent
						title='Price'
						onClear={() => {
							setPriceMinDraft(undefined)
							setPriceMaxDraft(undefined)
						}}
						onCancel={() => setPriceOpen(false)}
						onSave={() => {
							onPriceMinChange(priceMinDraft)
							onPriceMaxChange(priceMaxDraft)
							setPriceOpen(false)
							onFiltersApply?.(beds, priceMinDraft, priceMaxDraft, moveIn)
						}}
					>
						<RangeContent
							min={priceMinBound}
							max={priceMaxBound}
							valueMin={priceMinDraft}
							valueMax={priceMaxDraft}
							onMinChange={setPriceMinDraft}
							onMaxChange={setPriceMaxDraft}
							step={PRICE_STEP}
							prefix='$'
						/>
					</FilterContent>
				</FilterButton>
			</FilterSlot>

			<FilterSlot
				visible={showDateFilter}
				showFilters={showFilters}
				slotRef={el => {
					itemRefs.current[dateSlot] = el
				}}
			>
				<FilterButton
					label='Date'
					icon='Calendar'
					minWidth={DATE_MIN_WIDTH}
					activeLabel={dateLabel}
					onClear={() => {
						onMoveInChange('')
						setDateOpen(false)
						onFiltersApply?.(beds, priceMin, priceMax, '')
					}}
					open={dateOpen}
					onOpenChange={handleDateOpenChange}
				>
					<FilterContent
						title='Move-in Date'
						onClear={() => setMoveInDraft('')}
						onCancel={() => setDateOpen(false)}
						onSave={() => {
							onMoveInChange(moveInDraft)
							onCalendarMonthChange(calendarMonthDraft, calendarYearDraft)
							setDateOpen(false)
							onFiltersApply?.(beds, priceMin, priceMax, moveInDraft)
						}}
					>
						<DateContent
							value={moveInDraft}
							onValueChange={setMoveInDraft}
							calendarMonth={calendarMonthDraft}
							calendarYear={calendarYearDraft}
							onCalendarMonthChange={(month, year) => {
								setCalendarMonthDraft(month)
								setCalendarYearDraft(year)
							}}
						/>
					</FilterContent>
				</FilterButton>
			</FilterSlot>

			{/* Custom filters from CMS config */}
			{customFilterConfigs.map((cf, cfIndex) => {
				const slug = cf.slug
				const selected = customFilterSelections[slug] ?? []
				const draft = customFilterDrafts[slug] ?? []
				const isOpen = customFiltersOpen[slug] ?? false
				const label = customFilterLabels[slug]
				const minWidth = cf.type === 'select' ? SINGLE_SELECT_MIN_WIDTH : MULTI_SELECT_MIN_WIDTH

				const handleClear = () => {
					const updated = { ...customFilterSelections, [slug]: [] }
					onCustomFilterSelectionsChange(updated)
					setCustomFiltersOpen(prev => ({ ...prev, [slug]: false }))
					onFiltersApply?.(beds, priceMin, priceMax, moveIn, undefined, updated)
				}

				const handleSave = () => {
					const updated = { ...customFilterSelections, [slug]: draft }
					onCustomFilterSelectionsChange(updated)
					setCustomFiltersOpen(prev => ({ ...prev, [slug]: false }))
					onFiltersApply?.(beds, priceMin, priceMax, moveIn, undefined, updated)
				}

				return (
					<FilterSlot
						key={slug}
						showFilters={showFilters}
						slotRef={el => { itemRefs.current[customSlot + cfIndex] = el }}
					>
						<FilterButton
							label={cf.name}
							minWidth={minWidth}
							activeLabel={label}
							additionalCount={cf.type === 'checkbox' && selected.length > 1 ? selected.length - 1 : undefined}
							onClear={handleClear}
							open={isOpen}
							onOpenChange={open => handleCustomFilterOpenChange(slug, open)}
						>
							<FilterContent
								title={cf.name}
								onClear={() => setCustomFilterDrafts(prev => ({ ...prev, [slug]: [] }))}
								onCancel={() => setCustomFiltersOpen(prev => ({ ...prev, [slug]: false }))}
								onSave={handleSave}
							>
								{cf.type === 'select' ? (
									<SingleSelectContent
										options={cf.options}
										selected={draft.length > 0 ? draft[0] : null}
										onChange={value => setCustomFilterDrafts(prev => ({ ...prev, [slug]: value ? [value] : [] }))}
										ariaLabel={cf.name}
									/>
								) : (
									<MultiSelectContent
										options={cf.options}
										selected={draft}
										onChange={values => setCustomFilterDrafts(prev => ({ ...prev, [slug]: values }))}
										ariaLabel={cf.name}
									/>
								)}
							</FilterContent>
						</FilterButton>
					</FilterSlot>
				)
			})}

			{/* Clear all — always measured; visible whenever individual filters are shown */}
			<FilterSlot
				showFilters={showFilters}
				slotRef={el => {
					itemRefs.current[clearSlot] = el
				}}
			>
				<Button
					label='Clear'
					appearance='ghost'
					color='content3'
					size='md'
					spacesClass='spaces-clear'
					onClick={onClearAll}
				/>
			</FilterSlot>

			{/* Spacer */}
			<div className='spaces:flex-1' />

			{/* Hidden measurement ref — always renders the full toggle for stable measurement */}
			<div
				ref={measureFixedRef}
				className='spaces:absolute spaces:invisible spaces:pointer-events-none'
				aria-hidden='true'
			>
				<ToggleButtonGroup type='single' value={viewMode} size='md'>
					<ToggleButton value='grid' iconOnly='Grid' aria-label='Grid view' />
					{showSplitView && <ToggleButton value='split' iconOnly='Split' aria-label='Split view' />}
					<ToggleButton value='map' iconOnly='Map' aria-label='Map view' />
				</ToggleButtonGroup>
			</div>

			{/* View mode toggle */}
			<div className='spaces-view-toggle spaces:shrink-0'>
				<ToggleButtonGroup
					type='single'
					value={viewMode}
					onValueChange={v => onViewModeChange(v as ViewMode)}
					size='md'
				>
					<Tooltip label='Card View'>
						<ToggleButton value='grid' iconOnly='Grid' aria-label='Grid view' />
					</Tooltip>
					{showSplitView && (
						<Tooltip label='Split View'>
							<ToggleButton value='split' iconOnly='Split' aria-label='Split view' />
						</Tooltip>
					)}
					<Tooltip label='Map View'>
						<ToggleButton value='map' iconOnly='Map' aria-label='Map view' />
					</Tooltip>
				</ToggleButtonGroup>
			</div>
		</div>
	)
}
